import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, X, Plus, Clock } from 'lucide-react';
import TimeEntryCard from './TimeEntryCard';
import TimeEntryCell from './TimeEntryCell';
import GroupedCalendarCell from './GroupedCalendarCell'; 

// Wrap the entire component in React.memo to prevent unnecessary re-renders
const CalendarView = React.memo(({ 
  timeEntries = [], 
  clients = [], 
  projects = [], 
  currentDate = new Date(), 
  viewMode = 'week', 
  groupBy = 'none', // Pass groupBy from parent
  onDateChange,
  onEntryEdit,
  onEntryDuplicate,
  onEntryDelete,
  onEntryView,
  onEntryCreate,
  onStatusUpdate,
  onAddComment,
  onUpdateTags,
  userRole,
  teamMembers,
  goals = { dailyHours: 8, weeklyHours: 40 }
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [newEntryDate, setNewEntryDate] = useState(null);
  const [newEntryValues, setNewEntryValues] = useState({
    task_name: '',
    duration_hours: 1,
    description: '',
    is_billable: true,
    client_id: '',
    project_id: ''
  });

  const handleEntryView = (entry) => {
    // Call the parent component's view handler
    onEntryView && onEntryView(entry);
  };
  
  // Generate calendar dates based on view mode (your existing function)
  const generateCalendarDates = () => {
    const dates = [];
    const start = new Date(currentDate);
    
    if (viewMode === 'day') {
      dates.push(new Date(start));
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(start);
      startOfWeek.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
    } else if (viewMode === 'month') {
      const startOfMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      const endOfMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      const startOfCalendar = new Date(startOfMonth);
      startOfCalendar.setDate(startOfMonth.getDate() - startOfMonth.getDay());
      
      for (let i = 0; i < 42; i++) {
        const date = new Date(startOfCalendar);
        date.setDate(startOfCalendar.getDate() + i);
        dates.push(date);
        if (date >= endOfMonth && date.getDay() === 6) break;
      }
    }
    
    return dates;
  };

  // Memoize calendarDates to avoid recompute on every render
  const calendarDates = useMemo(() => generateCalendarDates(), [currentDate, viewMode]);

  const getEntriesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return timeEntries.filter(entry => entry.entry_date === dateStr);
  };

  const getTotalHoursForDate = (date) => {
    const entries = getEntriesForDate(date);
    return entries.reduce((total, entry) => total + (entry.duration_hours || 0), 0);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    onDateChange(newDate);
  };

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const getProject = (projectId) => projects.find(p => p.id === projectId);
  const getClient = (clientId) => clients.find(c => c.id === clientId);

  const handleAddEntryClick = (date, e) => {
    if (e) e.stopPropagation();
    setNewEntryDate(date);
    setNewEntryValues({
      task_name: '',
      duration_hours: 1,
      description: '',
      is_billable: true,
      client_id: clients[0]?.id || '',
      project_id: projects[0]?.id || ''
    });
    setShowNewEntryModal(true);
  };

  const handleCreateEntry = () => {
    if (newEntryValues.task_name && newEntryDate) {
      const entryData = {
        ...newEntryValues,
        entry_date: newEntryDate.toISOString().split('T')[0],
        status: 'draft'
      };
      onEntryCreate(entryData);
      setShowNewEntryModal(false);
      setNewEntryDate(null);
    }
  };

  const handleCancelCreate = () => {
    setShowNewEntryModal(false);
    setNewEntryDate(null);
    setNewEntryValues({
      task_name: '',
      duration_hours: 1,
      description: '',
      is_billable: true,
      client_id: '',
      project_id: ''
    });
  };

  if (viewMode === 'day') {
    const entries = getEntriesForDate(currentDate);
    const totalHours = getTotalHoursForDate(currentDate);
    const goalProgress = (totalHours / goals.dailyHours) * 100;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-slate-800">
                {currentDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {totalHours > 0 ? `${formatTime(totalHours)} logged` : 'No time logged'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateDate(-1)}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDateChange(new Date())}
                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Today
              </button>
              <button
                onClick={() => navigateDate(1)}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
              <span>Daily Goal Progress</span>
              <span>{Math.round(goalProgress)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(goalProgress, 100)}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-6">
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-800 mb-2">No entries for this day</h4>
              <p className="text-slate-600 mb-4">Start tracking time to see your entries here</p>
              <button
                onClick={() => handleAddEntryClick(currentDate)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Entry
              </button>
            </div>
          ) : groupBy !== 'none' ? (
            // Grouped day view
            <GroupedCalendarCell
              entries={entries}
              groupBy={groupBy}
              clients={clients}
              projects={projects}
              teamMembers={teamMembers}
              onEdit={onEntryEdit}
              onDuplicate={onEntryDuplicate}
              onView={handleEntryView}
              onStatusUpdate={onStatusUpdate}
              onAddComment={onAddComment}
              onUpdateTags={onUpdateTags}
              userRole={userRole}
              teamMembers={teamMembers}
              isDayView={true}
            />
          ) : (
            <div className="space-y-4">
              {entries.map(entry => {
                const project = getProject(entry.project_id);
                const client = getClient(entry.client_id);
                return (
                  <TimeEntryCard
                    key={entry.id}
                    entry={{
                      ...entry,
                      comments: entry.comments || [],
                      tags: entry.tags || [],
                      priority: entry.priority || 'medium'
                    }}
                    clients={[client]}
                    projects={[project]}
                    onEdit={onEntryEdit}
                    onDuplicate={onEntryDuplicate}
                    onDelete={onEntryDelete}
                    onView={handleEntryView}
                    onStatusUpdate={onStatusUpdate}
                    onAddComment={onAddComment}
                    onUpdateTags={onUpdateTags}
                    userRole={userRole}
                    teamMembers={teamMembers}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-800">
              {currentDate.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric',
                ...(viewMode === 'week' && { day: 'numeric' })
              })}
            </h3>
            {viewMode === 'week' && (
              <p className="text-sm text-slate-600 mt-1">
                Week of {calendarDates[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateDate(-1)}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDateChange(new Date())}
              className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              Today
            </button>
            <button
              onClick={() => navigateDate(1)}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className={`grid gap-1 grid-cols-7`}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 bg-slate-50 rounded-t-lg">
              {day}
            </div>
          ))}
          
          {calendarDates.map((date, index) => {
            const entries = getEntriesForDate(date);
            const totalHours = getTotalHoursForDate(date);
            const isCurrentMonthDay = viewMode === 'week' || isCurrentMonth(date);
            const dayGoalProgress = (totalHours / goals.dailyHours) * 100;
            
            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors ${
                  !isCurrentMonthDay ? 'bg-slate-50 text-slate-400' : 'bg-white'
                } ${isToday(date) ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                onClick={() => setSelectedDate(date)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${isToday(date) ? 'text-blue-600' : 'text-slate-700'}`}>
                    {date.getDate()}
                  </span>
                  {totalHours > 0 && (
                    <span className="text-xs text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                      {formatTime(totalHours)}
                    </span>
                  )}
                </div>
                
                {isCurrentMonthDay && (
                  <div className="w-full bg-slate-200 rounded-full h-1 mb-2">
                    <div 
                      className={`h-1 rounded-full transition-all duration-300 ${
                        dayGoalProgress >= 100 ? 'bg-green-500' : 
                        dayGoalProgress >= 75 ? 'bg-blue-500' : 
                        dayGoalProgress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(dayGoalProgress, 100)}%` }}
                    />
                  </div>
                )}
                
                <div className="space-y-1 overflow-hidden">
                  {entries.length === 0 ? null : groupBy !== 'none' ? (
                    // Grouped mini-view for week/month
                    <GroupedCalendarCell
                      entries={entries}
                      groupBy={groupBy}
                      clients={clients}
                      projects={projects}
                      teamMembers={teamMembers}
                      onEdit={onEntryEdit}
                      onDuplicate={onEntryDuplicate}
                      onView={handleEntryView}
                      onStatusUpdate={onStatusUpdate}
                      onAddComment={onAddComment}
                      onUpdateTags={onUpdateTags}
                      userRole={userRole}
                      teamMembers={teamMembers}
                      isCompact={true}
                      maxEntriesPerGroup={1} // Limit for mini-view
                    />
                  ) : (
                    entries.slice(0, viewMode === 'month' ? 2 : 3).map(entry => (
                      <div key={entry.id} className="max-w-full">
                        <TimeEntryCell
                          entry={entry}
                          project={getProject(entry.project_id)}
                          client={getClient(entry.client_id)}
                          onEdit={onEntryEdit}
                          onDuplicate={onEntryDuplicate}
                          onView={handleEntryView}
                          isCompact={viewMode !== 'day'}
                        />
                      </div>
                    ))
                  )}
                  {entries.length > (viewMode === 'month' ? 2 : 3) && (
                    <div className="text-xs text-slate-500 text-center py-1">
                      +{entries.length - (viewMode === 'month' ? 2 : 3)} more
                    </div>
                  )}
                </div>
                
                {entries.length === 0 && isCurrentMonthDay && (
                  <button
                    onClick={(e) => handleAddEntryClick(date, e)}
                    className="w-full mt-2 p-2 text-xs text-slate-400 hover:text-slate-600 hover:bg-white rounded border-2 border-dashed border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <Plus className="w-3 h-3 mx-auto" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3 mb-6">
              {groupBy !== 'none' ? (
                // Grouped modal content
                <GroupedCalendarCell
                  entries={getEntriesForDate(selectedDate)}
                  groupBy={groupBy}
                  clients={clients}
                  projects={projects}
                  teamMembers={teamMembers}
                  onEdit={onEntryEdit}
                  onDuplicate={onEntryDuplicate}
                  onView={handleEntryView}
                  onStatusUpdate={onStatusUpdate}
                  onAddComment={onAddComment}
                  onUpdateTags={onUpdateTags}
                  userRole={userRole}
                  teamMembers={teamMembers}
                  isDayView={true}
                />
              ) : (
                getEntriesForDate(selectedDate).map(entry => (
                  <TimeEntryCell
                    key={entry.id}
                    entry={entry}
                    project={getProject(entry.project_id)}
                    client={getClient(entry.client_id)}
                    onEdit={onEntryEdit}
                    onDuplicate={onEntryDuplicate}
                    onView={handleEntryView}
                  />
                ))
              )}
              {getEntriesForDate(selectedDate).length === 0 && (
                <p className="text-slate-500 text-center py-4">No entries for this day</p>
              )}
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={() => {
                  handleAddEntryClick(selectedDate);
                  setSelectedDate(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Entry
              </button>
              <button
                onClick={() => setSelectedDate(null)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
	  
      {/* New Entry Modal */}
      {showNewEntryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Add Entry for {newEntryDate?.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button
                onClick={handleCancelCreate}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
                <select
                  value={newEntryValues.client_id}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, client_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
                <select
                  value={newEntryValues.project_id}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, project_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select project...</option>
                  {projects
                    .filter(project => !newEntryValues.client_id || project.client_id === newEntryValues.client_id)
                    .map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Name</label>
                <input
                  type="text"
                  value={newEntryValues.task_name}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, task_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter task name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (hours)</label>
                <input
                  type="number"
                  step="0.25"
                  value={newEntryValues.duration_hours}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={newEntryValues.description}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter description..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="newEntryBillable"
                  checked={newEntryValues.is_billable}
                  onChange={(e) => setNewEntryValues(prev => ({ ...prev, is_billable: e.target.checked }))}
                  className="mr-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="newEntryBillable" className="text-sm text-slate-700">
                  Billable
                </label>
              </div>
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={handleCancelCreate}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEntry}
                disabled={!newEntryValues.task_name || !newEntryValues.client_id || !newEntryValues.project_id}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Create Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

CalendarView.displayName = 'CalendarView'; // For debugging

export default CalendarView;